import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X, Phone, ArrowRight, Clock } from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { api } from "../../App";
import { toast } from "sonner";

const POPUP_EXIT_DELAY_MS = 800;
const POPUP_DISMISS_DELAY_MS = 2500;

const LeadCapturePopup = () => {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const prevPath = useRef(location.pathname);
  const isQuotePage = location.pathname === "/instant-quote";

  const showPopup = useCallback(() => {
    if (dismissed || submitted) return;
    const alreadyDismissed = sessionStorage.getItem("lead_popup_dismissed");
    if (alreadyDismissed) return;
    setVisible(true);
  }, [dismissed, submitted]);

  // Detect leaving the quote page (navigating away without completing)
  useEffect(() => {
    const wasOnQuote = prevPath.current === "/instant-quote";
    const leftQuote = !isQuotePage && wasOnQuote;
    prevPath.current = location.pathname;

    if (leftQuote && !location.pathname.startsWith("/payment")) {
      // Don't show if user already completed a quote this session
      const quoteCompleted = sessionStorage.getItem("quote_completed");
      if (quoteCompleted) return;
      const timer = setTimeout(showPopup, POPUP_EXIT_DELAY_MS);
      return () => clearTimeout(timer);
    }
  }, [location.pathname, isQuotePage, showPopup]);

  // Also trigger on mouse leaving viewport (desktop exit intent)
  useEffect(() => {
    if (!isQuotePage) return;
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0) showPopup();
    };
    document.addEventListener("mouseleave", handleMouseLeave);
    return () => document.removeEventListener("mouseleave", handleMouseLeave);
  }, [isQuotePage, showPopup]);

  const handleDismiss = () => {
    setVisible(false);
    setDismissed(true);
    sessionStorage.setItem("lead_popup_dismissed", "1");
  };

  const handleCallbackRequest = async () => {
    if (!phone || phone.length < 8) {
      toast.error("Please enter a valid phone number");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", {
        name: "Callback Request",
        email: "callback@request.com",
        phone,
        message: "Abandoned quote flow - requested callback",
        type: "callback_request"
      });
      setSubmitted(true);
      toast.success("We'll call you back shortly!");
      setTimeout(handleDismiss, POPUP_DISMISS_DELAY_MS);
    } catch {
      toast.error("Something went wrong. Please call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReturnToQuote = () => {
    setVisible(false);
    navigate("/instant-quote");
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="lead-capture-popup">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleDismiss} />
      <div className="relative bg-white max-w-md w-full p-0 shadow-2xl animate-in fade-in zoom-in-95">
        {/* Close */}
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 z-10"
          data-testid="lead-popup-close"
        >
          <X className="w-5 h-5" />
        </button>

        {submitted ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 mx-auto flex items-center justify-center mb-4">
              <Phone className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-['Outfit'] font-bold text-[#0F172A] mb-2">
              We'll call you back!
            </h3>
            <p className="text-slate-600 text-sm">
              One of our team will be in touch within the hour.
            </p>
          </div>
        ) : (
          <>
            {/* Header band */}
            <div className="bg-[#FF5A1F] p-5 text-center">
              <h3 className="text-xl font-['Outfit'] font-bold text-white mb-1">
                Don't leave without your quote!
              </h3>
              <p className="text-white/90 text-sm">
                Get your price in 60 seconds — or we'll call you
              </p>
            </div>

            <div className="p-6 space-y-4">
              {/* Return to quote */}
              <Button
                onClick={handleReturnToQuote}
                className="w-full btn-primary h-12 text-base"
                data-testid="lead-popup-return-btn"
              >
                Finish My Quote
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>

              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-slate-200" />
                <span className="text-xs text-slate-400 uppercase tracking-wider">or</span>
                <div className="flex-1 h-px bg-slate-200" />
              </div>

              {/* Callback request */}
              <div>
                <p className="text-sm font-medium text-[#0F172A] mb-2">
                  Want us to call you instead?
                </p>
                <div className="flex gap-2">
                  <Input
                    type="tel"
                    placeholder="Your phone number"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="flex-1 h-11"
                    data-testid="lead-popup-phone"
                  />
                  <Button
                    onClick={handleCallbackRequest}
                    disabled={submitting}
                    variant="outline"
                    className="h-11 px-4 font-semibold"
                    data-testid="lead-popup-callback-btn"
                  >
                    <Phone className="w-4 h-4 mr-1" />
                    Call Me
                  </Button>
                </div>
              </div>

              <div className="flex items-center gap-2 text-xs text-slate-500 pt-2">
                <Clock className="w-3 h-3" />
                <span>We typically respond within 15 minutes during business hours</span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LeadCapturePopup;
